/**
 * Планиране — what is coming.
 *
 * Four sections, general to specific: the numbers that drive a decision, the
 * twelve-month shape, when each obligation ends, then the obligations
 * themselves.
 *
 * Nothing on this page is money that has moved. It reads the plan; balances
 * and reports are untouched by it.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Pencil,
  Plus,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, Skeleton } from '@/components/ui/misc';
import { CardSkeleton, EmptyState, ErrorState, PageHeader } from '@/components/common';
import { CommitmentDialog } from '@/components/CommitmentDialog';
import { PayInstalmentDialog } from '@/components/PayInstalmentDialog';
import { CommitmentTimeline } from '@/components/CommitmentTimeline';
import { colorClasses } from '@/lib/colors';
import { formatDate, formatMonth, formatMonthShort, formatMoney } from '@/lib/format';
import { KIND_LABELS, FREQUENCY_LABELS } from '@/lib/schedule';
import { useCommitments, useForecast, useUpcoming } from '@/hooks/useApi';
import type { Commitment, UpcomingInstalment } from '@/lib/types';

export function PlanPage(): JSX.Element {
  const [editing, setEditing] = useState<Commitment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paying, setPaying] = useState<UpcomingInstalment | null>(null);

  const { data: commitments = [], isLoading, error, refetch } = useCommitments();
  const { data: forecast } = useForecast(12);
  const { data: upcoming } = useUpcoming(30);

  const openEdit = (c: Commitment | null) => {
    setEditing(c);
    setDialogOpen(true);
  };

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <CardSkeleton count={4} />
        <Skeleton className="h-72 w-full rounded-lg" />
      </div>
    );
  }

  const base = forecast?.baseCurrency ?? 'EUR';
  const dec = forecast?.baseDecimals ?? 2;
  const money = (m: number) => formatMoney(m, base, dec);

  return (
    <div>
      <PageHeader
        title="План"
        description="Регулярни приходи и задължения — какво предстои и кога изтича."
        actions={
          <>
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link to="/plan/calendar">
                <CalendarDays className="h-4 w-4" />
                Календар
              </Link>
            </Button>
            <Button size="sm" className="gap-2" onClick={() => openEdit(null)}>
              <Plus className="h-4 w-4" />
              Добави
            </Button>
          </>
        }
      />

      {commitments.length === 0 ? (
        <Card>
          <EmptyState
            icon={CalendarDays}
            title="Още няма нищо планирано"
            description="Добави заплатата си и кредитите/лизингите, за да видиш какво остава свободно всеки месец и кога изтича кое."
            action={<Button onClick={() => openEdit(null)}>Добави първото</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {/* ── 1. Числата ── */}
          {forecast ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Месечен приход"
                value={money(forecast.monthlyIncomeMinor)}
                tone="income"
                icon={TrendingUp}
              />
              <StatTile
                label="Месечни задължения"
                value={money(forecast.monthlyExpenseMinor)}
                tone="expense"
                icon={TrendingDown}
              />
              <StatTile
                label="Свободно на месец"
                value={money(forecast.realisticFreeMinor)}
                tone={forecast.realisticFreeMinor >= 0 ? 'income' : 'expense'}
                sub={
                  forecast.actualMonthlyExpenseMinor > 0
                    ? `след реалния разход ${money(forecast.actualMonthlyExpenseMinor)}/мес.`
                    : undefined
                }
              />
              <StatTile label="Остава да платя" value={money(forecast.outstandingMinor)} />
            </div>
          ) : null}

          {/* The honesty note. A planner that hides this flatters the user. */}
          {forecast && forecast.actualMonthlyExpenseMinor > 0 ? (
            <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Само по задълженията свободното излиза{' '}
              <span className="tabular">{money(forecast.freeMinor)}</span>, но за последните 6
              месеца реално си харчил още{' '}
              <span className="tabular">{money(forecast.actualMonthlyExpenseMinor)}</span> месечно
              за ежедневни неща. „Свободно" горе е след тях.
            </p>
          ) : null}

          {/* ── Просрочено ── */}
          {upcoming && upcoming.overdueCount > 0 ? (
            <Card className="border-destructive/40">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <p className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                  <span>
                    <span className="font-medium">{upcoming.overdueCount}</span> просрочени вноски
                    на стойност <span className="tabular">{money(upcoming.overdueMinor)}</span>
                  </span>
                </p>
              </CardContent>
            </Card>
          ) : null}

          {/* ── 2. Барчарт ── */}
          {forecast && forecast.months.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Следващите 12 месеца</CardTitle>
              </CardHeader>
              <CardContent>
                <ForecastChart forecast={forecast} />
              </CardContent>
            </Card>
          ) : null}

          {/* ── 3. Времева линия ── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Кога изтича кое</CardTitle>
            </CardHeader>
            <CardContent>
              <CommitmentTimeline
                commitments={commitments}
                baseCurrency={base}
                baseDecimals={dec}
                onSelect={openEdit}
              />
            </CardContent>
          </Card>

          {/* ── 4. Списък ── */}
          <section>
            <h2 className="mb-3 font-semibold">Задължения и приходи</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {commitments.map((c) => (
                <CommitmentCard
                  key={c.id}
                  c={c}
                  onEdit={() => openEdit(c)}
                  onPay={() => {
                    const next = upcoming?.items.find((i) => i.commitmentId === c.id);
                    if (next) setPaying(next);
                  }}
                  payable={Boolean(upcoming?.items.some((i) => i.commitmentId === c.id))}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      <CommitmentDialog open={dialogOpen} editing={editing} onOpenChange={setDialogOpen} />
      <PayInstalmentDialog instalment={paying} onClose={() => setPaying(null)} />
    </div>
  );
}

function StatTile({
  label,
  value,
  sub,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'income' | 'expense';
  icon?: React.ComponentType<{ className?: string }>;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
          {label}
        </p>
        <p
          className={cn(
            'tabular mt-1 text-xl font-semibold',
            tone === 'income' && 'text-income',
            tone === 'expense' && 'text-expense',
          )}
        >
          {value}
        </p>
        {sub ? <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function ForecastChart({
  forecast,
}: {
  forecast: NonNullable<ReturnType<typeof useForecast>['data']>;
}): JSX.Element {
  const d = 10 ** forecast.baseDecimals;

  // One stacked series per commitment, so a heavy month shows WHY it is heavy.
  const seriesKeys = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of forecast.months) {
      for (const b of m.byCommitment) map.set(b.name, b.color);
    }
    return [...map.entries()];
  }, [forecast]);

  const data = useMemo(
    () =>
      forecast.months.map((m) => {
        const row: Record<string, number | string> = {
          month: m.month,
          Приход: m.incomeMinor / d,
        };
        for (const [name] of seriesKeys) row[name] = 0;
        for (const b of m.byCommitment) row[b.name] = b.amountMinor / d;
        return row;
      }),
    [forecast, seriesKeys, d],
  );

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="month"
            tickFormatter={formatMonthShort}
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelFormatter={(v: string) => formatMonth(v)}
            formatter={(v: number, name: string) => [
              formatMoney(Math.round(v * d), forecast.baseCurrency, forecast.baseDecimals),
              name,
            ]}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />

          {/* The reference line is the point of the chart: obligations below it
              are comfortable, above it are not. */}
          {forecast.actualMonthlyExpenseMinor > 0 ? (
            <ReferenceLine
              y={forecast.actualMonthlyExpenseMinor / d}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="4 4"
              label={{
                value: 'обичаен разход',
                position: 'insideTopRight',
                fontSize: 10,
                fill: 'hsl(var(--muted-foreground))',
              }}
            />
          ) : null}

          {seriesKeys.map(([name, color]) => (
            <Bar
              key={name}
              dataKey={name}
              stackId="commitments"
              fill={colorClasses(color).hex}
              radius={[2, 2, 0, 0]}
            />
          ))}

          <Line
            type="monotone"
            dataKey="Приход"
            stroke="hsl(var(--income))"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function CommitmentCard({
  c,
  onEdit,
  onPay,
  payable,
}: {
  c: Commitment;
  onEdit: () => void;
  onPay: () => void;
  payable: boolean;
}): JSX.Element {
  const colors = colorClasses(c.color);
  const openEnded = c.lastDueDate == null;

  return (
    <Card className={cn(c.overdueCount > 0 && 'border-destructive/40')}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', colors.bg)} />
            <span className="truncate font-medium">{c.name}</span>
            <Badge variant="neutral" className="shrink-0 text-[10px]">
              {KIND_LABELS[c.kind]}
            </Badge>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onEdit} aria-label="Промени">
            <Pencil className="h-4 w-4" />
          </Button>
        </div>

        <p
          className={cn(
            'tabular mt-2 text-xl font-semibold',
            c.direction === 'INCOME' ? 'text-income' : '',
          )}
        >
          {formatMoney(c.amountMinor, c.currency, c.decimals)}
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">
            {FREQUENCY_LABELS[c.frequency].toLowerCase()}
          </span>
        </p>

        <p className="mt-1 text-xs text-muted-foreground">
          {openEnded ? (
            <>Безсрочно{c.nextDueDate ? ` · следващо ${formatDate(c.nextDueDate)}` : ''}</>
          ) : (
            <>
              остават {c.remainingCount} вноски · последна {formatDate(c.lastDueDate!)}
              {c.direction === 'EXPENSE' ? (
                <>
                  {' '}
                  · остава общо{' '}
                  <span className="tabular">
                    {formatMoney(c.remainingMinor, c.currency, c.decimals)}
                  </span>
                </>
              ) : null}
            </>
          )}
        </p>

        {c.progress != null ? (
          <div className="mt-2">
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={cn('h-full transition-all', colors.bg)}
                style={{ width: `${Math.min(c.progress * 100, 100)}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              платени {c.paidCount} от {c.totalInstalments}
            </p>
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {c.overdueCount > 0 ? (
            <Badge variant="destructive" className="text-[10px]">
              {c.overdueCount} просрочени
            </Badge>
          ) : null}
          {!c.tracked ? (
            <Badge variant="neutral" className="text-[10px]">
              извън касите
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">
              {c.fundName} · {c.locationName}
            </span>
          )}
        </div>

        {payable ? (
          <Button variant="outline" size="sm" className="mt-3 w-full gap-2" onClick={onPay}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Отбележи следващата като платена
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
